import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, MapPin, Clock, Instagram, Facebook, Paperclip, Send, Mail } from "lucide-react";
import { Reveal } from "./Reveal";
import { CONTACT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border hairline bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-bronze";

export const Contact = () => {
  const [form, setForm] = useState({ emri: "", mbiemri: "", telefoni: "", email: "", pershkrimi: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("foto", file);
      const res = await axios.post(`${API}/contact`, fd);
      if (res.data.success) {
        toast.success("Kërkesa juaj u dërgua me sukses. Do t'ju kontaktojmë së shpejti.");
        setForm({ emri: "", mbiemri: "", telefoni: "", email: "", pershkrimi: "" });
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      } else {
        toast.error(res.data.message || "Diçka shkoi gabim. Provoni përsëri.");
      }
    } catch {
      toast.error("Dërgimi dështoi. Ju lutem provoni përsëri ose na telefononi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="bg-ink py-24 texture-grain sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-16 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
          <div>
            <Reveal>
              <p className="eyebrow mb-5">Kontakt</p>
              <h2 data-testid="contact-title" className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Keni një projekt në mendje?
              </h2>
              <p className="mt-6 text-base leading-relaxed text-white/60 sm:text-lg">
                Na kontaktoni për projektin tuaj dhe kërkoni ofertën tuaj.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <form onSubmit={submit} className="mt-12 space-y-5" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <input required value={form.emri} onChange={set("emri")} placeholder="Emri *" data-testid="contact-input-emri" className={inputCls} />
                  <input value={form.mbiemri} onChange={set("mbiemri")} placeholder="Mbiemri" data-testid="contact-input-mbiemri" className={inputCls} />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <input required value={form.telefoni} onChange={set("telefoni")} placeholder="Telefoni *" data-testid="contact-input-telefoni" className={inputCls} />
                  <input type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="contact-input-email" className={inputCls} />
                </div>
                <textarea
                  required
                  value={form.pershkrimi}
                  onChange={set("pershkrimi")}
                  placeholder="Përshkrimi i projektit *"
                  rows={5}
                  data-testid="contact-input-pershkrimi"
                  className={`${inputCls} resize-none`}
                />
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="contact-input-file"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="contact-file-button"
                    className="inline-flex items-center gap-3 border border-dashed border-white/20 px-5 py-3.5 text-sm text-white/55 transition-colors duration-300 hover:border-bronze/60 hover:text-bronze"
                  >
                    <Paperclip size={16} />
                    {file ? file.name : "Ngarkoni fotografi / skica (opsionale)"}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 border border-bronze bg-bronze px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-transparent hover:text-bronze disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {sending ? "Duke dërguar..." : "Kërko ofertë"}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <aside className="flex h-full flex-col justify-between gap-10 border hairline bg-graphite p-8 sm:p-10" data-testid="contact-info-panel">
              <div>
                <p className="font-display text-lg font-bold tracking-[0.15em] text-white">
                  ALBESI <span className="text-bronze">AXH</span> METAL
                </p>
                <p className="mt-2 text-sm text-white/50">Punime metalike me përvojë, cilësi dhe garanci</p>

                <div className="mt-8 space-y-5">
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-button" className="flex items-center gap-4 border hairline px-5 py-4 transition-colors duration-300 hover:border-bronze/50">
                    <Phone size={18} className="text-bronze" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Telefoni</p>
                      <p className="font-display text-base font-semibold text-white">{CONTACT.phoneDisplay}</p>
                    </div>
                  </a>
                  <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="flex items-center gap-4 border hairline px-5 py-4 transition-colors duration-300 hover:border-bronze/50">
                    <MessageCircle size={18} className="text-bronze" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-white/40">WhatsApp</p>
                      <p className="font-display text-base font-semibold text-white">Na shkruani direkt</p>
                    </div>
                  </a>
                  <a href={CONTACT.emailHref} data-testid="contact-email-button" className="flex items-center gap-4 border hairline px-5 py-4 transition-colors duration-300 hover:border-bronze/50">
                    <Mail size={18} className="text-bronze" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Email</p>
                      <p className="font-display text-base font-semibold text-white">{CONTACT.email}</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 border hairline px-5 py-4" data-testid="contact-address">
                    <MapPin size={18} className="text-bronze" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Adresa</p>
                      <p className="text-sm font-medium text-white">{CONTACT.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 border hairline px-5 py-4" data-testid="contact-hours">
                    <Clock size={18} className="mt-0.5 text-bronze" />
                    <div className="w-full">
                      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Orari i punës</p>
                      {CONTACT.hours.map((h) => (
                        <div key={h.day} className="mt-1.5 flex justify-between gap-6 text-sm">
                          <span className="text-white/60">{h.day}</span>
                          <span className="font-medium text-white">{h.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="border hairline p-3 text-white/60 transition-colors duration-300 hover:border-bronze/60 hover:text-bronze">
                  <Instagram size={18} />
                </a>
                <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="border hairline p-3 text-white/60 transition-colors duration-300 hover:border-bronze/60 hover:text-bronze">
                  <Facebook size={18} />
                </a>
                <span className="text-xs uppercase tracking-[0.2em] text-white/35">@albesi_axh_metal</span>
              </div>
            </aside>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="contact-map">
            <a href={CONTACT.mapLink} target="_blank" rel="noopener noreferrer" className="eyebrow mb-4 inline-block transition-colors hover:text-white">
              Na gjeni në hartë — Albesi-AXH, Rr. 101 bb, Çellopek
            </a>
            <div className="overflow-hidden border hairline">
              <iframe
                title="Harta e vendndodhjes — ALBESI AXH METAL, Çellopek"
                src={CONTACT.mapEmbed}
                className="h-[380px] w-full grayscale-[35%] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map-iframe"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
